import React, { useState, useCallback, useRef, useContext, useEffect } from 'react'
import { Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import TableAction from '../buttons/TableAction'
import { domainService } from '../../lib/services'
import AgDataTable from '../ag-data-table/DataTable'
import EditIcon from '@mui/icons-material/Edit'
import { ColDef, GetRowIdParams, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core'
import ManageCompaniesModal from './ManageCompaniesModal'
import makeStyles from '@mui/styles/makeStyles'
import { Trash } from 'genesis-suite/icons'
import { SwalContext } from 'genesis-suite/components'
import { logEvent } from '../../lib/amplitudeClient'
import { DomainContext } from '../contexts/DomainContext'

const tableColumns: ColDef[] = [
    {
        pinned: 'left',
        colId: 'checkbox',
        lockPosition: 'left',
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        resizable: false,
        width: 20,
        minWidth: 50,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
    },
    { headerName: 'ID', field: 'id', width: 90, hide: true },
    { headerName: 'Company Name', field: 'name', flex: 1, editable: false, filter: false },
    { headerName: 'Domain Name', field: 'domainName', flex: 1, editable: false, filter: false },
    { headerName: 'Admin Email', field: 'adminEmailAddress', flex: 1, editable: false, filter: false },
    { headerName: 'Country Name', field: 'address.countryName', width: 150, editable: false, filter: false },
    { headerName: 'Province Name', field: 'address.provinceName', width: 150, editable: false, filter: false },
    { headerName: 'City Name', field: 'address.cityName', width: 150, editable: false, filter: false },
    { headerName: 'Postal Code', field: 'address.postalCode', width: 150, editable: false, filter: false },
]

const useStyles = makeStyles(({ palette, spacing }) => ({
    actionButton: {
        marginLeft: spacing(1),
        backgroundColor: palette.primary.main,
        '&:hover': {
            backgroundColor: palette.primary.light,
        },
        '&$disabled': {
            backgroundColor: palette.action.disabledBackground,
        },
    },
    disabled: {},
}))

export default function ManageCompanies() {
    const [gridStatus, setGridStatus] = useState<string>('')
    const [gridAPI, setGridAPI] = useState<GridApi>(null)
    const dataTableRef = useRef(null)
    const [showManageCompaniesModal, setShowManageCompaniesModal] = useState(false)
    const [selectedCompany, setSelectedCompany] = useState(null)
    const [hasEditCompany, setHasEditCompany] = useState(false)
    const classes = useStyles({})
    const { confirm, alert } = useContext(SwalContext)
    const { companies, loading } = useContext(DomainContext)

    useEffect(() => {
        if (gridAPI) {
            if (loading) {
                gridAPI?.showLoadingOverlay()
            } else {
                gridAPI?.hideOverlay()
                gridAPI?.setGridOption('serverSideDatasource', createDatasource())
            }
        }
    }, [loading, gridAPI])

    const createDatasource = (searchText = null) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                try {
                    let response = companies ?? []

                    if (searchText !== null) {
                        response = response.filter(company =>
                            company?.name?.toLowerCase().includes(searchText.toLowerCase())
                        )
                    }

                    if (loading) {
                        params.api.showLoadingOverlay()
                    } else if (Number(response?.length) === 0) {
                        params.api.showNoRowsOverlay()
                    } else {
                        params.api.hideOverlay()
                    }

                    setGridStatus(`Showing 1 - ${response.length} of ${response.length} companies`)
                    params.success({ rowData: response })
                } catch (error) {
                    console.error(error)
                    params.fail()
                }
            },
        }
    }

    const handleCompanyCheckbox = useCallback(event => {
        if (event.node.isSelected()) {
            setSelectedCompany(event.data)
        } else {
            if (event.api.getSelectedNodes().length === 0) {
                setSelectedCompany(null)
            }
        }
    }, [])

    const getRowId = useCallback((params: GetRowIdParams) => params.data.id, [])

    const onGridReady = useCallback((params: { api: GridApi }) => {
        setGridAPI(params.api)
        params.api.setGridOption('serverSideDatasource', createDatasource())
    }, [])

    const handleSearch = value => {
        gridAPI.setGridOption('serverSideDatasource', createDatasource(value))
    }
    const handleDeleteCompany = async () => {
        const response = await confirm(
            'Deleting the selected company will leave all the apps & users of this company orphaned. This operation cannot be undone. Do you want to proceed?',
            {
                type: 'warning',
                confirmButtonProps: { text: 'Delete' },
            }
        )
        if (response.dismiss) return
        const companyId = selectedCompany?.id
        if (!companyId) {
            alert('There were no company selected to delete!', { type: 'error' })
            return
        }
        try {
            await domainService.deleteCompany(companyId)
            gridAPI?.refreshServerSide()
            gridAPI?.deselectAll()
            gridAPI?.refreshCells()
            setSelectedCompany(null)
            logEvent('Delete Users')
        } catch (err) {
            alert(err.Message || err.message, { type: 'error' })
        }
    }

    const AddButton = () => (
        <TableAction
            title="Add Company"
            classes={{
                root: classes.actionButton,
                disabled: classes.disabled,
            }}
            size="large"
            onClick={() => {
                setShowManageCompaniesModal(true)
                setHasEditCompany(false)
            }}
        >
            <AddIcon />
        </TableAction>
    )
    const EditButton = () => (
        <TableAction
            classes={{
                root: classes.actionButton,
                disabled: classes.disabled,
            }}
            title="Edit Company"
            size="large"
            disabled={selectedCompany === null || !selectedCompany}
            onClick={() => {
                setShowManageCompaniesModal(true)
                setHasEditCompany(true)
            }}
        >
            <EditIcon />
        </TableAction>
    )

    const DeleteCompany = () => (
        <TableAction
            classes={{
                root: classes.actionButton,
                disabled: classes.disabled,
            }}
            title="Delete Company"
            size="large"
            disabled={selectedCompany === null || !selectedCompany}
            onClick={() => {
                handleDeleteCompany()
            }}
        >
            <Trash />
        </TableAction>
    )

    return (
        <Box sx={{ height: '100%', p: 1 }}>
            <AgDataTable
                ref={dataTableRef}
                onGridReady={onGridReady}
                columns={tableColumns}
                extraActions={[<AddButton key="add" />, <EditButton key="edit" />, <DeleteCompany key="delete" />]}
                title="Manage Companies"
                onRowSelected={handleCompanyCheckbox}
                gridStatus={gridStatus}
                handleSearch={handleSearch}
                getRowId={getRowId}
                rowSelection="single"
            />
            {showManageCompaniesModal && (
                <ManageCompaniesModal
                    open={showManageCompaniesModal}
                    onClose={() => {
                        setShowManageCompaniesModal(false)
                    }}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    hasEditCompany={hasEditCompany}
                    setHasEditCompany={setHasEditCompany}
                    gridAPI={gridAPI}
                />
            )}
        </Box>
    )
}
